// US-BUCKET-04 -- Edit a guardian-managed bucket.
//
// Reachable from the guardian's home screen via an "Edit" action on
// any guardian-managed bucket tile. The form pre-populates with the
// bucket's current values, shows a live rebalance preview of any
// percentage or flag change (using the shared `rebalanceForEditedBucket`
// so the preview matches the backend exactly), and submits via
// `bucketUpdate`.
//
// Guardian-only. Child-managed buckets open a different screen (US-BUCKET-08).

import {
  type AppError,
  type BucketEntry,
  type BucketUpdateOutput,
  type InterestPeriodCode,
  bucketBalanceAdjust,
  bucketUpdate,
} from '@chorepop/core/client';
import { rebalanceForEditedBucket } from '@chorepop/core/domain';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { ColourPicker } from '../components/ColourPicker';
import { ToggleSwitch } from '../components/ToggleSwitch';

type Props = {
  sessionId: string;
  bucket: BucketEntry;
  existingBuckets: BucketEntry[];
  /** Display currency for the balance adjustment section. */
  currency: string;
  actorIsChild?: boolean;
  onSaved: (result: BucketUpdateOutput) => void;
  /** Called after a successful balance adjustment so the caller can
   *  refetch bucket data and flash a message. */
  onBalanceAdjusted?: (newBalanceMinorUnits: number) => void;
  onCancel: () => void;
  /** Called when a data inconsistency is detected and the user wants to
   *  repair it via the split editor. If not provided, the screen falls
   *  back to the generic "close and reopen" message. */
  onFixSplits?: () => void;
};

export const EditBucketScreen: Component<Props> = (props) => {
  const [bucketName, setBucketName] = createSignal(props.bucket.bucketName);
  const [displayColor, setDisplayColor] = createSignal<string | null>(props.bucket.displayColor);
  const [percentStr, setPercentStr] = createSignal((props.bucket.allocationBps / 100).toString());
  const [percentIsFixed, setPercentIsFixed] = createSignal(props.bucket.percentIsFixed);
  const [priority, setPriority] = createSignal(props.bucket.priority.toString());
  const [requiresApprovalToWithdraw, setRequiresApprovalToWithdraw] = createSignal(
    props.bucket.requiresApprovalToWithdraw,
  );
  // BUG-024: signal for the is_guardian_managed flag. US-BUCKET-13's AC
  // explicitly listed this as flippable from the Edit form for guardian
  // actors (0 → 1 "adopts" a child-created bucket; 1 → 0 "donates" a
  // guardian-created bucket to the child). The backend already supports
  // it via UpdateBucketFields; only the UI was missing.
  const [isGuardianManaged, setIsGuardianManaged] = createSignal(props.bucket.isGuardianManaged);
  // US-INTEREST-01 -- interest configuration seeded
  // from the current bucket. Children never see the form (gated below
  // with <Show when={!props.actorIsChild}>) and the backend rejects
  // any of these fields submitted on the child-self path.
  const [interestEnabled, setInterestEnabled] = createSignal(props.bucket.interestEnabled);
  // Display the rate as a percent string (e.g. "2.5") rather than raw
  // bps. The form converts on submit. This matches how allocationBps
  // is handled -- store as percent string in UI, convert to bps on
  // wire.
  const [interestRateStr, setInterestRateStr] = createSignal(
    (props.bucket.interestRateBps / 100).toString(),
  );
  const [interestPeriodCode, setInterestPeriodCode] = createSignal<InterestPeriodCode | ''>(
    props.bucket.interestPeriodCode ?? '',
  );
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  // US-BUCKET-15 -- balance adjustment state (guardian only).
  const [balanceStr, setBalanceStr] = createSignal(
    (props.bucket.balanceMinorUnits / 100).toFixed(2),
  );
  const [balanceReason, setBalanceReason] = createSignal('');
  const [balanceSubmitting, setBalanceSubmitting] = createSignal(false);
  const [balanceError, setBalanceError] = createSignal<string | null>(null);
  const [balanceConfirmDecrease, setBalanceConfirmDecrease] = createSignal(false);

  const newBalanceMinorUnits = createMemo(() => {
    const f = Number.parseFloat(balanceStr().replace(/[^0-9.]/g, ''));
    if (Number.isNaN(f) || f < 0) return null;
    return Math.round(f * 100);
  });

  const balanceDelta = createMemo(() => {
    const nb = newBalanceMinorUnits();
    if (nb === null) return null;
    return nb - props.bucket.balanceMinorUnits;
  });

  function formatMoney(minorUnits: number): string {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: props.currency,
      }).format(minorUnits / 100);
    } catch {
      return `${props.currency} ${(minorUnits / 100).toFixed(2)}`;
    }
  }

  const handleBalanceSubmit = async () => {
    const nb = newBalanceMinorUnits();
    if (nb === null) return;
    const reason = balanceReason().trim();
    if (!reason) {
      setBalanceError('Reason is required.');
      return;
    }
    if (reason.length > 120) {
      setBalanceError('Reason must be 120 characters or fewer.');
      return;
    }
    const delta = balanceDelta() ?? 0;
    if (delta < 0 && !balanceConfirmDecrease()) {
      setBalanceConfirmDecrease(true);
      return;
    }
    setBalanceSubmitting(true);
    setBalanceError(null);
    try {
      const result = await bucketBalanceAdjust({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
        newBalanceMinorUnits: nb,
        reason,
        sourceCode: 'balance_adjustment',
      });
      if (!result.ok) {
        setBalanceError(humanReadableError(result.error));
        return;
      }
      props.onBalanceAdjusted?.(result.value.newBalanceMinorUnits);
    } finally {
      setBalanceSubmitting(false);
    }
  };

  // US-BUCKET-13 (2026-04-25): the previous `readOnly` derivation
  // (true when guardian + child-managed) was removed. Guardians now
  // edit any bucket they can see, including child-created ones.
  // Children only ever land here for their own child-managed buckets;
  // the `bucket_update` command's role gate enforces that at the
  // backend boundary, so the UI doesn't need to repeat the check.

  const allocationBps = createMemo(() => {
    const trimmed = percentStr().trim();
    if (trimmed.length === 0) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  });

  // Live preview -- same function the backend uses.
  const preview = createMemo(() => {
    const bps = allocationBps();
    if (bps === null || bps <= 0 || bps >= 10000) return null;
    const existing = props.existingBuckets.map((b) => ({
      bucketId: b.bucketId,
      allocationBps: b.allocationBps,
      percentIsFixed: b.percentIsFixed,
      sortOrder: b.sortOrder,
      priority: b.priority,
    }));
    return rebalanceForEditedBucket(existing, props.bucket.bucketId, bps);
  });

  // What actually changed? Build a diff so we only send mutations.
  const diff = createMemo(() => {
    const bps = allocationBps();
    // US-INTEREST-01 -- derive the new interest rate (bps) from the
    // form's percent string. Empty string OR non-finite parses as
    // current value (no change). Negative + huge are caught by the
    // backend; the UI shows the error if it returns.
    const rateTrimmed = interestRateStr().trim();
    const parsedRateBps =
      rateTrimmed.length === 0 || !Number.isFinite(Number(rateTrimmed))
        ? props.bucket.interestRateBps
        : Math.round(Number(rateTrimmed) * 100);
    // Period: empty string (UI default for "no period") maps to null
    // which clears the column. The backend BucketUpdateInput uses
    // Some(None) for "clear" via the Option<Option<String>> shape;
    // the TS wire shape encodes that as `interestPeriodCode: null`.
    const formPeriod: InterestPeriodCode | null =
      interestPeriodCode() === '' ? null : (interestPeriodCode() as InterestPeriodCode);
    return {
      nameChanged: bucketName().trim() !== props.bucket.bucketName,
      colorChanged: displayColor() !== props.bucket.displayColor,
      percentChanged: bps !== null && bps !== props.bucket.allocationBps,
      fixedChanged: percentIsFixed() !== props.bucket.percentIsFixed,
      approvalChanged: requiresApprovalToWithdraw() !== props.bucket.requiresApprovalToWithdraw,
      // BUG-024: same diff treatment as the other flag flips.
      guardianManagedChanged: isGuardianManaged() !== props.bucket.isGuardianManaged,
      // BUG-035: the guardian interest checkbox represents credited
      // interest. Legacy display-only buckets are upgraded on their next
      // save so every future accrual changes the bucket balance.
      interestEnabledChanged: interestEnabled() !== props.bucket.interestEnabled,
      interestRateChanged: parsedRateBps !== props.bucket.interestRateBps,
      interestPeriodChanged: formPeriod !== props.bucket.interestPeriodCode,
      interestActuallyCreditsChanged: interestEnabled() !== props.bucket.interestActuallyCredits,
      priorityChanged:
        Number.parseInt(priority(), 10) !== props.bucket.priority &&
        !Number.isNaN(Number.parseInt(priority(), 10)),
      newPriority: Number.parseInt(priority(), 10),
      bps,
      parsedRateBps,
      formPeriod,
    };
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    const trimmedName = bucketName().trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Please enter a name for the bucket.');
      return;
    }
    if ([...trimmedName].length > 50) {
      setErrorMessage('Bucket name must be 50 characters or fewer.');
      return;
    }
    const bps = allocationBps();
    if (bps === null || bps <= 0 || bps >= 10000) {
      setErrorMessage('Percentage must be between 0.01 and 99.99.');
      return;
    }

    const d = diff();
    if (
      !d.nameChanged &&
      !d.colorChanged &&
      !d.percentChanged &&
      !d.fixedChanged &&
      !d.approvalChanged &&
      !d.guardianManagedChanged &&
      !d.interestEnabledChanged &&
      !d.interestRateChanged &&
      !d.interestPeriodChanged &&
      !d.interestActuallyCreditsChanged &&
      !d.priorityChanged
    ) {
      // No-op. Just close.
      props.onCancel();
      return;
    }

    setSubmitting(true);
    try {
      const result = await bucketUpdate({
        sessionId: props.sessionId,
        bucketId: props.bucket.bucketId,
        ...(d.nameChanged ? { bucketName: trimmedName } : {}),
        ...(d.colorChanged ? { displayColor: displayColor() } : {}),
        ...(d.percentChanged && d.bps !== null ? { allocationBps: d.bps } : {}),
        ...(d.fixedChanged ? { percentIsFixed: percentIsFixed() } : {}),
        ...(d.approvalChanged ? { requiresApprovalToWithdraw: requiresApprovalToWithdraw() } : {}),
        ...(d.guardianManagedChanged ? { isGuardianManaged: isGuardianManaged() } : {}),
        // US-INTEREST-01 -- only send a field when it changed. The
        // backend's UpdateBucketFields uses Option<T> per-field; absent
        // = leave alone.
        ...(d.interestEnabledChanged ? { interestEnabled: interestEnabled() } : {}),
        ...(d.interestRateChanged ? { interestRateBps: d.parsedRateBps } : {}),
        ...(d.interestPeriodChanged ? { interestPeriodCode: d.formPeriod } : {}),
        ...(d.interestActuallyCreditsChanged ? { interestActuallyCredits: interestEnabled() } : {}),
        ...(d.priorityChanged ? { priority: d.newPriority } : {}),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      props.onSaved(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      class="action-screen--bucket-form"
      eyebrow="Bucket settings"
      title={props.bucket.bucketName}
      onBack={props.onCancel}
    >
      <p class="action-screen-intro">
        Adjust this bucket's name, colour, percentage, or behaviour. Percentage changes redistribute
        across other flexible buckets without changing existing balances. Interest payments are
        added to this bucket and increase its balance.
      </p>

      <form onSubmit={handleSubmit} novalidate>
        <label class="field">
          <span class="field-label">Bucket name</span>
          <input
            type="text"
            autocomplete="off"
            required
            disabled={submitting()}
            value={bucketName()}
            onInput={(e) => {
              setBucketName(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="edit-bucket-name"
          />
        </label>

        <ColourPicker
          value={displayColor()}
          onChange={(c) => {
            setDisplayColor(c);
          }}
          disabled={submitting()}
        />

        <label class="field">
          <span class="field-label">Percentage</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            max="99.99"
            inputmode="decimal"
            required
            disabled={submitting()}
            value={percentStr()}
            onInput={(e) => {
              setPercentStr(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="edit-bucket-percent"
          />
        </label>

        {/* BUG-021 + BUG-024 -- same fieldset pattern as AddBucketScreen.
              The bucket-flags-group reads as a related set rather than
              continuous lines. The is_guardian_managed toggle is now
              exposed (was missing despite US-BUCKET-13's AC explicitly
              listing it). The percent_is_fixed and is_guardian_managed
              flags are guardian-only -- children can't flip them
              (enforced backend-side). The withdrawals-need-approval flag
              is shown to both roles. */}
        <fieldset class="bucket-flags-group">
          <legend>Bucket behaviour</legend>

          <Show when={!props.actorIsChild}>
            <ToggleSwitch
              label="Fixed percentage"
              description="Locked, so it won't shrink when more buckets are added later."
              checked={percentIsFixed()}
              onChange={setPercentIsFixed}
              disabled={submitting()}
              testId="edit-bucket-fixed"
            />

            <ToggleSwitch
              label="Guardian-managed"
              description='Turn on to "adopt" a child-created bucket; turn off so the child can manage it themselves.'
              checked={isGuardianManaged()}
              onChange={setIsGuardianManaged}
              disabled={submitting()}
              testId="edit-bucket-guardian-managed"
            />
          </Show>

          <ToggleSwitch
            label="Withdrawals need approval"
            checked={requiresApprovalToWithdraw()}
            onChange={setRequiresApprovalToWithdraw}
            disabled={submitting()}
            testId="edit-bucket-requires-approval"
          />
        </fieldset>

        {/* Priority: controls which bucket absorbs the floor value on uneven splits.
              Higher number = more likely to receive the ceiling value.
              Auto-assigned on creation; editable here for both roles. */}
        <div class="form-field">
          <label class="form-label" for="edit-bucket-priority">
            Rounding priority
          </label>
          <input
            id="edit-bucket-priority"
            type="number"
            class="form-input form-input--narrow"
            min="1"
            step="1"
            disabled={submitting()}
            value={priority()}
            onInput={(e) => setPriority(e.currentTarget.value)}
            data-testid="edit-bucket-priority"
          />
          <p class="form-hint">
            Higher = receives the whole percentage on uneven splits. Lower = absorbs the fractional
            remainder. Guardian-managed buckets are auto-assigned higher values than child-managed
            ones.
          </p>
        </div>

        {/* US-INTEREST-01 / BUG-035 -- guardian-only credited interest.
              Children never see this block and the backend rejects interest
              fields on the child-self path. Interest is the one binary
              setting intentionally rendered as a native checkbox. */}
        <Show when={!props.actorIsChild}>
          <fieldset class="bucket-flags-group">
            <legend>Interest</legend>

            <label class="bucket-interest-checkbox">
              <input
                type="checkbox"
                checked={interestEnabled()}
                disabled={submitting()}
                onChange={(event) => setInterestEnabled(event.currentTarget.checked)}
                data-testid="edit-bucket-interest-enabled"
              />
              <span>
                <strong>{interestEnabled() ? 'Interest is on' : 'Enable interest'}</strong>
                <small>Interest payments are added to this bucket and change its balance.</small>
              </span>
            </label>

            <Show when={interestEnabled()}>
              <label class="field">
                <span class="field-label">Rate (% per period)</span>
                <input
                  type="text"
                  inputmode="decimal"
                  autocomplete="off"
                  placeholder="e.g. 2.5"
                  disabled={submitting()}
                  value={interestRateStr()}
                  onInput={(e) => setInterestRateStr(e.currentTarget.value)}
                  data-testid="edit-bucket-interest-rate"
                />
                <span class="field-hint">
                  A 2.5% monthly rate would be entered as <code>2.5</code>. Stored internally as
                  basis points (250 bps).
                </span>
              </label>

              <label class="field">
                <span class="field-label">Period</span>
                <select
                  disabled={submitting()}
                  value={interestPeriodCode()}
                  onChange={(e) =>
                    setInterestPeriodCode(e.currentTarget.value as InterestPeriodCode | '')
                  }
                  data-testid="edit-bucket-interest-period"
                >
                  <option value="">-- pick a period --</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </label>
            </Show>
          </fieldset>
        </Show>

        <Show when={preview()}>
          {(p) => (
            <Show
              when={p().ok}
              fallback={
                <div class="form-error" role="alert" data-testid="edit-bucket-preview-error">
                  {formatPreviewError(p())}
                </div>
              }
            >
              <div class="rebalance-preview" data-testid="edit-bucket-preview">
                <Show
                  when={(p() as { ok: true; value: unknown[] }).value.length > 0}
                  fallback={<div class="preview-heading">No other buckets will change.</div>}
                >
                  <div class="preview-heading">After this change:</div>
                  <ul>
                    <For
                      each={
                        (
                          p() as {
                            ok: true;
                            value: { bucketId: number; newAllocationBps: number }[];
                          }
                        ).value
                      }
                    >
                      {(rr) => {
                        const before = props.existingBuckets.find(
                          (b) => b.bucketId === rr.bucketId,
                        );
                        if (!before) return null;
                        return (
                          <li data-testid={`edit-preview-row-${before.bucketId}`}>
                            <span class="preview-name">{before.bucketName}</span>
                            <span class="preview-change">
                              {(before.allocationBps / 100).toFixed(2)}% →{' '}
                              {(rr.newAllocationBps / 100).toFixed(2)}%
                            </span>
                          </li>
                        );
                      }}
                    </For>
                  </ul>
                </Show>
              </div>
            </Show>
          )}
        </Show>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert" data-testid="edit-bucket-error">
              {msg()}
              <Show when={props.onFixSplits && msg().includes('split editor')}>
                {' '}
                <button
                  type="button"
                  class="ghost"
                  style="margin-top: var(--space-2); display: block;"
                  onClick={() => props.onFixSplits?.()}
                  data-testid="edit-bucket-fix-splits"
                >
                  Open split editor to repair →
                </button>
              </Show>
            </div>
          )}
        </Show>

        <button
          type="submit"
          class="primary"
          disabled={submitting()}
          data-testid="edit-bucket-submit"
        >
          {submitting() ? 'Saving…' : 'Save changes'}
        </button>
      </form>

      {/* US-BUCKET-15 -- balance adjustment (guardian only). Shown as a
            separate section so it's visually distinct from the metadata
            edit above and clearly labelled with its own save action. */}
      <Show when={!props.actorIsChild}>
        <hr class="edit-bucket-divider" />
        <section class="edit-bucket-balance-section">
          <h2 class="edit-bucket-balance-heading">Balance</h2>
          <p class="edit-bucket-balance-intro">
            Current balance:{' '}
            <strong class="num">{formatMoney(props.bucket.balanceMinorUnits)}</strong>
            {'. '}
            Set a new balance directly -- useful for corrections or initial setup. This doesn't go
            through the deposit flow.
          </p>

          <label class="field">
            <span class="field-label">New balance</span>
            <input
              type="number"
              min="0"
              step="0.01"
              class="num"
              value={balanceStr()}
              onInput={(e) => {
                setBalanceStr(e.currentTarget.value);
                setBalanceConfirmDecrease(false);
                setBalanceError(null);
              }}
              disabled={balanceSubmitting()}
              data-testid="edit-bucket-balance-input"
            />
          </label>

          <Show when={balanceDelta() !== null && balanceDelta() !== 0}>
            <div
              class={`bucket-adjust-delta ${(balanceDelta() ?? 0) > 0 ? 'bucket-adjust-delta--increase' : 'bucket-adjust-delta--decrease'}`}
            >
              {(balanceDelta() ?? 0) > 0 ? '▲' : '▼'} {formatMoney(Math.abs(balanceDelta() ?? 0))}
              {(balanceDelta() ?? 0) > 0 ? ' increase' : ' decrease'}
            </div>
          </Show>

          <label class="field">
            <span class="field-label">
              Reason <span class="field-required">*</span>
            </span>
            <input
              type="text"
              placeholder="e.g. Correction -- entered wrong amount"
              maxlength="120"
              value={balanceReason()}
              onInput={(e) => {
                setBalanceReason(e.currentTarget.value);
                setBalanceError(null);
              }}
              disabled={balanceSubmitting()}
              data-testid="edit-bucket-balance-reason"
            />
            <span class="field-hint">{balanceReason().length}/120</span>
          </label>

          <Show when={balanceError()}>
            <div class="form-error" role="alert">
              {balanceError()}
            </div>
          </Show>

          <Show when={balanceConfirmDecrease()}>
            <div class="form-warning" role="alert">
              This will decrease the balance by{' '}
              <strong>{formatMoney(Math.abs(balanceDelta() ?? 0))}</strong>. Confirm?
            </div>
          </Show>

          <button
            type="button"
            class="primary"
            onClick={() => void handleBalanceSubmit()}
            disabled={
              balanceSubmitting() ||
              newBalanceMinorUnits() === null ||
              balanceReason().trim().length === 0
            }
            data-testid="edit-bucket-balance-submit"
          >
            {balanceSubmitting()
              ? 'Saving…'
              : balanceConfirmDecrease()
                ? 'Confirm decrease'
                : 'Update balance'}
          </button>
        </section>
      </Show>
    </ActionScreen>
  );
};

function formatPreviewError(outcome: ReturnType<typeof rebalanceForEditedBucket>): string {
  if (outcome.ok) return '';
  const e = outcome.error;
  switch (e.kind) {
    case 'newBucketBpsOutOfRange':
      return 'Percentage must be between 0.01 and 99.99.';
    case 'noFlexibleBuckets':
      return 'No other flexible buckets to absorb this change. Make a different bucket flexible first.';
    case 'flexiblePoolTooSmall':
      return `Requested ${(e.requestedBps / 100).toFixed(2)}%, but after other fixed buckets are deducted only ${(e.availableBps / 100).toFixed(2)}% remain for this one. Reduce a different fixed bucket first.`;
    case 'currentAllocationsDoNotSumTo10000':
      return `Allocations are currently ${(e.actual / 100).toFixed(2)}% — they will be rebalanced when you save. Use the Allocations screen to adjust all buckets at once.`;
  }
}

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'not_found':
      return error.message;
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
